/**
 * Application Routes
 *
 * Root routing configuration with lazy loading for optimal performance.
 */

import { Routes } from '@angular/router';
import { authGuard, publicGuard, managePlanPageGuard, customerAdminGuard, profileCompleteGuard } from './core/guards';

export const routes: Routes = [
  {
    // No guard: a transient bounce page opened in a popup to relay the LinkedIn OAuth
    // code/state back to the opener window; must load regardless of auth state.
    path: 'linkedin-callback',
    title: 'Signing in…',
    loadComponent: () => import('./features/pages/linkedin-callback/linkedin-callback.component').then(m => m.LinkedInCallbackComponent)
  },

  {
    path: '',
    canActivate: [publicGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Sign In',
        loadComponent: () => import('./features/pages/login/login').then(m => m.LoginComponent)
      },
      {
        path: 'signup',
        title: 'Sign Up',
        loadComponent: () => import('./features/pages/signup/signup').then(m => m.SignupComponent)
      },
      {
        path: 'verify-email',
        title: 'Verify Email',
        loadComponent: () => import('./features/pages/verify-email/verify-email').then(m => m.VerifyEmailComponent)
      },
      {
        path: 'forgot-password',
        title: 'Forgot Password',
        loadComponent: () => import('./features/pages/forgot-password/forgot-password').then(m => m.ForgotPasswordComponent)
      },
      {
        path: 'reset-password',
        title: 'Reset Password',
        loadComponent: () => import('./features/pages/reset-password/reset-password').then(m => m.ResetPasswordComponent)
      },
      {
        path: 'magic-link',
        title: 'Sign In',
        loadComponent: () => import('./features/pages/magic-link/magic-link').then(m => m.MagicLinkComponent)
      },
      {
        path: 'accept-invitation',
        title: 'Accept Invitation',
        loadComponent: () => import('./features/pages/accept-invitation/accept-invitation.component').then(m => m.AcceptInvitationComponent)
      }
    ]
  },

  {
    path: 'complete-profile',
    title: 'Complete Your Profile',
    canActivate: [authGuard],
    loadComponent: () => import('./features/pages/complete-profile/complete-profile.component').then(m => m.CompleteProfileComponent)
  },

  {
    path: '',
    loadComponent: () => import('./core/layouts/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    canActivate: [authGuard, profileCompleteGuard],
    children: [
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./features/pages/dashboard/dashboard').then(m => m.DashboardComponent)
      },
      {
        path: 'app-reviews',
        title: 'Reply to Reviews',
        loadComponent: () => import('./features/pages/reply-to-reviews/reply-to-reviews.component').then(a => a.ReplyToReviewsComponent),
      },
      {
        path: 'app-reviews/summary',
        redirectTo: 'app-reviews',
        pathMatch: 'full',
      },
      {
        path: 'app-reviews/reply',
        redirectTo: 'app-reviews',
        pathMatch: 'full',
      },
      {
        path: 'app-health',
        title: 'App Health',
        loadComponent: () => import('./features/pages/app-health/app-health.component').then(c => c.AppHealthComponent),
      },
      {
        path: 'app-health/detail/:type',
        title: 'App Health Detail',
        loadComponent: () => import('./features/pages/app-health-detail/app-health-detail.component').then(c => c.AppHealthDetailComponent),
      },
      {
        path: 'compare',
        title: 'Compare',
        loadComponent: () => import('./features/pages/compare/compare.component').then(c => c.CompareComponent),
      },
      {
        path: 'user-management',
        title: 'User Management',
        canActivate: [customerAdminGuard],
        loadComponent: () => import('./features/pages/user-management/user-management.component').then(c => c.UserManagementComponent),
      },
      {
        path: 'user-information',
        redirectTo: 'user-management',
        pathMatch: 'full'
      },
      {
        path: 'user-information/:id',
        redirectTo: 'user-management',
        pathMatch: 'full'
      },
      {
        path: 'profile',
        title: 'Profile',
        loadComponent: () => import('./features/pages/profile/profile.component').then(c => c.ProfileComponent),
      },
      {
        path: 'notifications',
        title: 'Notifications',
        loadComponent: () => import('./features/pages/notifications/notifications.component').then(c => c.NotificationsComponent),
      },
      {
        path: 'subscription',
        title: 'Subscription',
        canActivate: [managePlanPageGuard],
        loadComponent: () => import('./features/pages/subscription/subscription.component').then(c => c.SubscriptionComponent),
      },
      {
        path: 'subscription/payment',
        title: 'Payment',
        canActivate: [managePlanPageGuard],
        loadComponent: () => import('./features/pages/subscription-payment/subscription-payment.component').then(c => c.SubscriptionPaymentComponent),
      },
      {
        path: 'audit-log',
        title: 'Audit Log',
        loadComponent: () => import('./features/pages/audit-log/audit-log.component').then(c => c.AuditLogComponent),
      },
      {
        path: 'reports',
        title: 'Reports',
        loadComponent: () => import('./features/pages/reports/reports.component').then(c => c.ReportsComponent),
      },
      {
        path: 'reports/schedule-email/:reportType',
        title: 'Schedule Email Delivery',
        loadComponent: () => import('./features/pages/schedule-email-delivery/schedule-email-delivery.component').then(c => c.ScheduleEmailDeliveryComponent),
      },
      {
        path: 'configuration',
        redirectTo: 'configuration/app-connections',
        pathMatch: 'full'
      },
      {
        path: 'configuration/app-connections',
        title: 'App Connections',
        loadComponent: () => import('./features/pages/app-connections/app-connections.component').then(c => c.AppConnectionsComponent),
      },
      {
        path: 'configuration/app-connections/new',
        title: 'New App Connection',
        loadComponent: () => import('./features/pages/app-connections/new-connection/new-connection.component').then(c => c.NewConnectionComponent),
      },
      {
        path: 'configuration/reply-templates',
        title: 'Reply Templates',
        loadComponent: () => import('./features/pages/reply-templates/reply-templates.component').then(c => c.ReplyTemplatesComponent),
      },
      {
        path: 'configuration/tags',
        title: 'Tags',
        loadComponent: () => import('./features/pages/tags/tags.component').then(c => c.TagsComponent),
      },
      {
        path: 'insights/total-customers',
        title: 'Total Customers',
        loadComponent: () => import('./features/pages/insights/total-customers/total-customers.component').then(c => c.TotalCustomersComponent),
      },
      {
        path: 'insights/user-engagement',
        title: 'User Engagement',
        loadComponent: () => import('./features/pages/insights/user-engagement/user-engagement.component').then(c => c.UserEngagementComponent),
      },
      {
        path: 'insights/session-duration',
        title: 'Session Duration',
        loadComponent: () => import('./features/pages/insights/session-duration/session-duration.component').then(c => c.SessionDurationComponent),
      },
      {
        path: 'insights/peak-hours',
        title: 'Peak Hours',
        loadComponent: () => import('./features/pages/insights/peak-hours/peak-hours.component').then(c => c.PeakHoursComponent),
      },
      {
        path: 'insights/sentiment-analysis',
        title: 'Sentiment Analysis',
        loadComponent: () => import('./features/pages/insights/sentiment-analysis/sentiment-analysis.component').then(c => c.SentimentAnalysisComponent),
      },
      {
        path: 'insights/user-demographics',
        title: 'User Demographics',
        loadComponent: () => import('./features/pages/insights/user-demographics/user-demographics.component').then(c => c.UserDemographicsComponent),
      },
      {
        path: 'insights/top-region',
        title: 'Top Region',
        loadComponent: () => import('./features/pages/insights/top-region/top-region.component').then(c => c.TopRegionComponent),
      },
      {
        path: 'insights/active-users',
        title: 'Active Users',
        loadComponent: () => import('./features/pages/insights/active-users/active-users.component').then(c => c.ActiveUsersComponent),
      },
      {
        path: 'insights/churn-rate',
        title: 'Churn Rate',
        loadComponent: () => import('./features/pages/insights/churn-rate/churn-rate.component').then(c => c.ChurnRateComponent),
      },
      {
        path: 'insights/engagement-trends',
        title: 'Engagement Trends',
        loadComponent: () => import('./features/pages/insights/engagement-trends/engagement-trends.component').then(c => c.EngagementTrendsComponent),
      }
    ]
  },
];
